import { observer } from 'mobx-react-lite';
import { FC, useEffect, useRef, useState } from 'react';
import {
  KpiDropdownActions,
  KpiEditTypes,
  KpiType,
  ModalViews,
  StorePerformanceInterval,
  UpdateKpiRequest,
  createStorePerformanceRequest,
} from '../../../constants';
import { useEventContext } from '../../../global-state/eventContext';
import { KpiTargetsProps, useAsyncAction, useStores, useUser } from '../../../hooks';
import { isFailureResponse } from '../../../utils';
import KpiModal from '../../kpiModal/KpiModal';
import { KpiModalDetails } from '../../kpiTarget/KpiTarget';
import { CelebrationData, CelebrationModal } from '../common/celebrationModal/CelebrationModal';
import { WidgetType, WidgetsCategory } from '../types';
import { createGetIsWidgetVisible, createGetWidgetOrder } from '../util';
import { AppCustomers } from '../widgets/customerBaseValue/appCustomers/AppCustomers';
import { NewMembersWidget } from '../widgets/customerBaseValue/newMembers/NewMembersWidget';
import { SCOShareOfReceipts } from '../widgets/customerBaseValue/scoShareOfReceipts/SCOShareOfReceipts';
import { ShareOfReceipts } from '../widgets/customerBaseValue/shareOfReceipts/ShareOfReceipts';
import { TopSellingGarmentWidget } from '../widgets/customerBaseValue/topSellingGarment/TopSellingGarmentWidget';
import { BopisWidget } from '../widgets/customerExperience/bopis/BopisWidget';
import { CustomerEngagementWidget } from '../widgets/customerExperience/customerEngagement/CustomerEngagement';
import { CustomerFeedbackWidget } from '../widgets/customerExperience/customerFeedback/CustomerFeedbackWidget';
import { HelpASaleWidget } from '../widgets/customerExperience/helpASale/HelpASaleWidget';
import { OrisCustomersWidget } from '../widgets/customerExperience/orisCustomers/OrisCustomersWidget';
import { QueueLengthWidget } from '../widgets/customerExperience/queueLength/QueueLengthWidget';
import { RfidApprovalsWidget } from '../widgets/customerExperience/rfidApprovals/RfidApprovalsWidget';
import { RfidCountWidget } from '../widgets/customerExperience/rfidCountWidget/RfidCountWidget';
import { RfidSalesRetaggingWidget } from '../widgets/customerExperience/rfidSalesRetagging/RfidSalesRetaggingWidget';
import { StockAccuracyWidget } from '../widgets/customerExperience/stockAccuracy/StockAccuracyWidget';
import AreaComparisonWidget from '../widgets/salesAndEfficiency/areaComparison/AreaComparison';
import { AveragePurchaseWidget } from '../widgets/salesAndEfficiency/averagePurchase/AveragePurchaseWidget';
import { ConversionRateWidget } from '../widgets/salesAndEfficiency/conversionRate/ConversionRateWidget';
import { EfficiencyWidget } from '../widgets/salesAndEfficiency/efficiency/EfficiencyWidget';
import { PiecesPerReceiptWidget } from '../widgets/salesAndEfficiency/piecesPerReceipt/PiecesPerReceiptWidget';
import { SalesInsightsWidget } from '../widgets/salesAndEfficiency/salesInsights/SalesInsightsWidget';
import { BopisLeadTimeWidget } from '../widgets/customerExperience/bopisLeadTime/BopisLeadTimeWidget';
import { SalesNetWidget } from '../widgets/salesAndEfficiency/salesNet/SalesNetWidget';
import { WidgetCard } from './WidgetCard';
import { Container } from './WidgetsContainer.styles';

export type WidgetEnable = {
  [key: string]: boolean;
};

export type WidgetsContainerProps = {
  showAreaWidget: boolean;
  selectedCategory: WidgetsCategory;
  selectedDate: Date;
  isToday: boolean;
  isNonSigninUser?: boolean;
  interval?: StorePerformanceInterval;
};

export const WidgetsContainer: FC<WidgetsContainerProps> = observer(
  ({
    showAreaWidget,
    selectedCategory,
    selectedDate,
    isToday,
    isNonSigninUser,
    interval = StorePerformanceInterval.DAILY,
  }) => {
    const {
      set,
      get: { highlightedKpiTargets, userPermissions },
    } = useUser();

    const { transactionEventsCount } = useEventContext();
    const { fetchKpiTargets, updateKpiData } = useStores();

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [highestWidgetOrder, setHighestWidgetOrder] = useState(0);

    const [modalView, setModalView] = useState<ModalViews>(ModalViews.INITIAL);
    const [kpiModalDetails, setKpiModalDetails] = useState<Omit<KpiModalDetails, 'isAreaCap'>>({
      isModalOpen: false,
      kpiType: 'dailyConversionRate',
      operation: KpiEditTypes.ADD,
    });

    const [showCelebration, setShowCelebration] = useState(false);
    const [celebrationData, setCelebrationData] = useState<CelebrationData[]>([]);

    useEffect(() => {
      if (showCelebration) {
        const timer = setTimeout(() => {
          setShowCelebration(false);
        }, 10000); // 5 seconds

        return () => clearTimeout(timer);
      }
    }, [showCelebration]);

    const handleTargetAchieved = (data: CelebrationData[]) => {
      setCelebrationData((prev) => [...prev, ...data]);
      setShowCelebration(true);
    };

    const {
      isLoading: kpiTargetsLoading,
      isError: kpiTargetsError,
      triggerAction: triggerFetchKpiTargets,
    } = useAsyncAction<KpiTargetsProps>();

    const {
      result: updatedKpiTargetProps,
      isLoading: updateTargetLoading,
      triggerAction: triggerUpdateTarget,
    } = useAsyncAction<KpiTargetsProps>();

    // Update Target
    const updateTarget = async (updateRequest: UpdateKpiRequest[]) => {
      const response = await updateKpiData(updateRequest);

      if (isFailureResponse(response)) {
        setModalView(ModalViews.ERROR);
        throw new Error('Error updating KPI Target');
      }
      const { data } = response;
      setModalView(ModalViews.SUCCESS);
      return data;
    };

    const handleDropdownDetails = (kpiType: KpiType, action: KpiDropdownActions) => {
      const operationType = action.includes('add') ? KpiEditTypes.ADD : KpiEditTypes.UPDATE;
      setKpiModalDetails({
        isModalOpen: true,
        kpiType,
        operation: operationType,
      });
    };

    // Fetch KPI Targets
    const fetchTargets = async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate: selectedDate,
      });
      const response = await fetchKpiTargets(request);
      if (isFailureResponse(response)) {
        throw new Error('Error fetching KPI Targets');
      }

      const { data } = response;
      set({ kpiTargetsProps: data });

      return response.data;
    };

    // Update the kpiTargetsProps state when updatedKpiTargetProps changes
    useEffect(() => {
      if (updatedKpiTargetProps) {
        set({ kpiTargetsProps: updatedKpiTargetProps });
      }
    }, [updatedKpiTargetProps]);

    useEffect(() => {
      triggerFetchKpiTargets(fetchTargets);
    }, [selectedDate]);

    useEffect(() => {
      const containerEl = containerRef.current;

      if (!containerEl) return;

      const widgetCardEls = containerEl.querySelectorAll('[data-widget-card-order]');

      if (widgetCardEls.length === 0) return;

      let highestOrder = 0;

      widgetCardEls.forEach((el) => {
        const order = el.getAttribute('data-widget-card-order');

        if (order === null || Number.isNaN(Number.parseInt(order))) return;

        const elOrder = Number.parseInt(order);

        if (elOrder > highestOrder) highestOrder = elOrder;
      });

      setHighestWidgetOrder(highestOrder);
    });

    const hasPermissionToEditKpi = userPermissions.kpiTargets.canUpdate;

    const { getWidgetOrder } = createGetWidgetOrder(selectedCategory, highlightedKpiTargets);
    const { getIsWidgetVisible } = createGetIsWidgetVisible({
      userPermissions,
      selectedCategory,
      highlightedKpiTargets,
      getIsWidgetDisabled: () => false, // for now all widgets will be enabled for all markets
    });

    return (
      <Container ref={containerRef} data-testid="widgets-container">
        <KpiModal
          isOpen={kpiModalDetails.isModalOpen}
          onKpiModalClose={() => {
            setKpiModalDetails({
              ...kpiModalDetails,
              isModalOpen: false,
            });
            setModalView(ModalViews.INITIAL);
          }}
          kpiType={kpiModalDetails.kpiType}
          updateType={kpiModalDetails.operation}
          modalView={modalView}
          onKpiModalRetry={() => setModalView(ModalViews.INITIAL)}
          isModalLoading={updateTargetLoading}
          handleUpdateKpi={(updateRequest) => {
            triggerUpdateTarget(() => updateTarget(updateRequest));
          }}
        />
        <CelebrationModal
          isOpen={showCelebration}
          onClose={() => {
            setShowCelebration(false);
            setCelebrationData([]);
          }}
          celebrations={celebrationData}
        />

        {/* New Members Widget */}
        {getIsWidgetVisible(WidgetType.NewMembers) && (
          <WidgetCard
            name={WidgetType.NewMembers}
            col={1.5}
            order={getWidgetOrder(WidgetType.NewMembers)}
            highestOrder={highestWidgetOrder}
          >
            <NewMembersWidget
              interval={interval}
              currentDate={selectedDate}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(targetValue, updatedDate) =>
                handleTargetAchieved([
                  {
                    widgetName: 'New Members',
                    targetValue,
                    updateDate: updatedDate,
                  },
                ])
              }
            />
          </WidgetCard>
        )}

        {/* ConversionRate Widget */}
        {getIsWidgetVisible(WidgetType.ConversionRate) && (
          <WidgetCard
            name={WidgetType.ConversionRate}
            col={2}
            order={getWidgetOrder(WidgetType.ConversionRate)}
            highestOrder={highestWidgetOrder}
          >
            <ConversionRateWidget
              interval={interval}
              currentDate={selectedDate}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              isNonSigninUser={isNonSigninUser}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(targetValue, updatedDate) =>
                handleTargetAchieved([
                  {
                    widgetName: 'Conversion Rate',
                    targetValue,
                    updateDate: updatedDate,
                  },
                ])
              }
            />
          </WidgetCard>
        )}

        {/* Sales Net Widget v2 */}
        {getIsWidgetVisible(WidgetType.HourlyTurnover) && (
          <WidgetCard
            name={WidgetType.HourlyTurnover}
            col={2}
            order={getWidgetOrder(WidgetType.HourlyTurnover)}
            highestOrder={highestWidgetOrder}
          >
            <SalesNetWidget
              interval={interval}
              currentDate={selectedDate}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(widgetName, targetValue, updatedDate) =>
                handleTargetAchieved([{ widgetName, targetValue, updateDate: updatedDate }])
              }
            />
          </WidgetCard>
        )}

        {/* Top Selling Garment Widget */}
        {getIsWidgetVisible(WidgetType.TopSellingGarment) && (
          <WidgetCard
            name={WidgetType.TopSellingGarment}
            col={1.5}
            order={getWidgetOrder(WidgetType.TopSellingGarment)}
            highestOrder={highestWidgetOrder}
          >
            <TopSellingGarmentWidget
              currentDate={selectedDate}
              isNonSigninUser={isNonSigninUser}
              interval={interval}
            />
          </WidgetCard>
        )}

        {/* ShareOfReceipts Widget */}
        {getIsWidgetVisible(WidgetType.ShareOfReceipts) && (
          <WidgetCard
            name={WidgetType.ShareOfReceipts}
            col={1.5}
            order={getWidgetOrder(WidgetType.ShareOfReceipts)}
            highestOrder={highestWidgetOrder}
          >
            <ShareOfReceipts
              interval={interval}
              currentDate={selectedDate}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              transactionEventsCount={transactionEventsCount}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(targetValue, updatedDate) =>
                handleTargetAchieved([
                  {
                    widgetName: 'Share of Receipts',
                    targetValue,
                    updateDate: updatedDate,
                  },
                ])
              }
            />
          </WidgetCard>
        )}

        {/* SCO ShareOfReceipts Widget */}
        {getIsWidgetVisible(WidgetType.SCOShareOfReceipts) && (
          <WidgetCard
            name={WidgetType.SCOShareOfReceipts}
            col={1.5}
            order={getWidgetOrder(WidgetType.SCOShareOfReceipts)}
            highestOrder={highestWidgetOrder}
          >
            <SCOShareOfReceipts
              interval={interval}
              currentDate={selectedDate}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              transactionEventsCount={transactionEventsCount}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(targetValue, updatedDate) =>
                handleTargetAchieved([
                  {
                    widgetName: 'SCO Share of Receipts',
                    targetValue,
                    updateDate: updatedDate,
                  },
                ])
              }
            />
          </WidgetCard>
        )}

        {/* App Customers Widget */}
        {getIsWidgetVisible(WidgetType.AppCustomersWidget) && (
          <WidgetCard
            name={WidgetType.AppCustomersWidget}
            col={1}
            order={getWidgetOrder(WidgetType.AppCustomersWidget)}
            highestOrder={highestWidgetOrder}
          >
            <AppCustomers interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Pieces Per Receipt Widget */}
        {getIsWidgetVisible(WidgetType.PiecesPerReceipt) && (
          <WidgetCard
            name={WidgetType.PiecesPerReceipt}
            col={1.5}
            order={getWidgetOrder(WidgetType.PiecesPerReceipt)}
            highestOrder={highestWidgetOrder}
          >
            <PiecesPerReceiptWidget interval={interval} currentDate={selectedDate} isToday={isToday} />
          </WidgetCard>
        )}

        {/* Average Purchase Widget */}
        {getIsWidgetVisible(WidgetType.AveragePurchaseWidget) && (
          <WidgetCard
            name={WidgetType.AveragePurchaseWidget}
            col={1.5}
            order={getWidgetOrder(WidgetType.AveragePurchaseWidget)}
            highestOrder={highestWidgetOrder}
          >
            <AveragePurchaseWidget interval={interval} currentDate={selectedDate} isToday={isToday} />
          </WidgetCard>
        )}

        {/* Area Comparison Widget */}
        {getIsWidgetVisible(WidgetType.AreaComparisonWidget) && showAreaWidget && (
          <WidgetCard
            name={WidgetType.AreaComparisonWidget}
            col={2}
            order={getWidgetOrder(WidgetType.AreaComparisonWidget)}
            highestOrder={highestWidgetOrder}
          >
            <AreaComparisonWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Queue Length Widget */}
        {getIsWidgetVisible(WidgetType.QueueLengthWidget) && (
          <WidgetCard
            name={WidgetType.QueueLengthWidget}
            col={2}
            order={getWidgetOrder(WidgetType.QueueLengthWidget)}
            highestOrder={highestWidgetOrder}
          >
            <QueueLengthWidget
              interval={interval}
              currentDate={selectedDate}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(targetValue, updatedDate) =>
                handleTargetAchieved([
                  {
                    widgetName: 'Queue Length',
                    targetValue,
                    updateDate: updatedDate,
                  },
                ])
              }
            />
          </WidgetCard>
        )}

        {/* Customer Feedback Widget */}
        {getIsWidgetVisible(WidgetType.CustomerFeedBackWidget) && (
          <WidgetCard
            name={WidgetType.CustomerFeedBackWidget}
            col={1}
            order={getWidgetOrder(WidgetType.CustomerFeedBackWidget)}
            highestOrder={highestWidgetOrder}
          >
            <CustomerFeedbackWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* RFID Approvals Widget */}
        {getIsWidgetVisible(WidgetType.RfidApprovalsWidget) && (
          <WidgetCard
            name={WidgetType.RfidApprovalsWidget}
            col={2}
            order={getWidgetOrder(WidgetType.RfidApprovalsWidget)}
            highestOrder={highestWidgetOrder}
          >
            <RfidApprovalsWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* RFID Count Widget */}
        {getIsWidgetVisible(WidgetType.RfidCountWidget) && (
          <WidgetCard
            name={WidgetType.RfidCountWidget}
            col={1.5}
            order={getWidgetOrder(WidgetType.RfidCountWidget)}
            highestOrder={highestWidgetOrder}
          >
            <RfidCountWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* RFID Sales and Retagging */}
        {getIsWidgetVisible(WidgetType.RfidSalesRetagging) && (
          <WidgetCard
            name={WidgetType.RfidSalesRetagging}
            col={1}
            order={getWidgetOrder(WidgetType.RfidSalesRetagging)}
            highestOrder={highestWidgetOrder}
          >
            <RfidSalesRetaggingWidget
              interval={interval}
              currentDate={selectedDate}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              isToday={isToday}
              handleDropdownDetails={handleDropdownDetails}
            />
          </WidgetCard>
        )}

        {/* Stock Accuracy Widget */}
        {getIsWidgetVisible(WidgetType.StockAccuracyWidget) && (
          <WidgetCard
            name={WidgetType.StockAccuracyWidget}
            col={1.5}
            order={getWidgetOrder(WidgetType.StockAccuracyWidget)}
            highestOrder={highestWidgetOrder}
          >
            <StockAccuracyWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* ORIS Widget */}
        {getIsWidgetVisible(WidgetType.OrisCustomersWidget) && (
          <WidgetCard
            col={1.5}
            order={getWidgetOrder(WidgetType.OrisCustomersWidget)}
            name={WidgetType.OrisCustomersWidget}
            highestOrder={highestWidgetOrder}
          >
            <OrisCustomersWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Efficiency Widget */}
        {getIsWidgetVisible(WidgetType.EfficiencyWidget) && (
          <WidgetCard
            name={WidgetType.EfficiencyWidget}
            col={2}
            order={getWidgetOrder(WidgetType.EfficiencyWidget)}
            highestOrder={highestWidgetOrder}
          >
            <EfficiencyWidget isToday={isToday} interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Customer Engagement Widget */}
        {getIsWidgetVisible(WidgetType.CustomerEngagmentWidget) && (
          <WidgetCard
            name={WidgetType.CustomerEngagmentWidget}
            col={2}
            order={getWidgetOrder(WidgetType.CustomerEngagmentWidget)}
            highestOrder={highestWidgetOrder}
          >
            <CustomerEngagementWidget
              interval={interval}
              currentDate={selectedDate}
              isToday={isToday}
              isKpiLoading={kpiTargetsLoading}
              isKpiError={kpiTargetsError}
              hasPermissionToEditKpi={hasPermissionToEditKpi}
              handleDropdownDetails={handleDropdownDetails}
              onTargetAchieved={(targetValue, updatedDate) =>
                handleTargetAchieved([
                  {
                    widgetName: 'Customer Engagement',
                    targetValue,
                    updateDate: updatedDate,
                  },
                ])
              }
            />
          </WidgetCard>
        )}

        {/* Sales Insights Widget */}
        {getIsWidgetVisible(WidgetType.SalesInsightsWidget) && (
          <WidgetCard
            name={WidgetType.SalesInsightsWidget}
            col={2}
            order={getWidgetOrder(WidgetType.SalesInsightsWidget)}
            highestOrder={highestWidgetOrder}
          >
            <SalesInsightsWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Help A Sale Widget */}
        {getIsWidgetVisible(WidgetType.HelpASaleWidget) && (
          <WidgetCard
            name={WidgetType.HelpASaleWidget}
            col={1}
            order={getWidgetOrder(WidgetType.HelpASaleWidget)}
            highestOrder={highestWidgetOrder}
          >
            <HelpASaleWidget interval={interval} isToday={isToday} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Bopis Widget */}
        {getIsWidgetVisible(WidgetType.BopisWidget) && (
          <WidgetCard
            name={WidgetType.BopisWidget}
            col={1.5}
            order={getWidgetOrder(WidgetType.BopisWidget)}
            highestOrder={highestWidgetOrder}
          >
            <BopisWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}

        {/* Bopis Lead Time Widget */}
        {getIsWidgetVisible(WidgetType.BopisLeadTimeWidget) && (
          <WidgetCard
            name={WidgetType.BopisLeadTimeWidget}
            col={1.5}
            order={getWidgetOrder(WidgetType.BopisLeadTimeWidget)}
            highestOrder={highestWidgetOrder}
          >
            <BopisLeadTimeWidget interval={interval} currentDate={selectedDate} />
          </WidgetCard>
        )}
      </Container>
    );
  },
);
